import {InputTextarea} from "primereact/inputtextarea";
import {Message} from "primereact/message";
import {Toast} from "primereact/toast";
import {Tooltip} from "primereact/tooltip";
import {type ChangeEvent, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";

import {useSelectByCurrentLanguage} from "../../hooks/languageHooks";
import {
    APPRECIATION_KEYS_WITH_VARIANTS,
    type AppreciationKey,
    changeAppreciationValueAsync,
    getAppreciationId,
    selectAppreciationHasStoredValueCurried,
    selectAppreciationValueOrDefaultCurried,
    selectAppreciationVariantLabel,
    serializeKey,
} from "../../redux/appreciationsUtil";
import {selectReadOnly} from "../../redux/persistenceSlice";
import {useRevaDispatch} from "../../redux/revaHooks";
import {selectSingleApiParamOutputDefCurried} from "../../redux/specSelectors";
import {truncateText} from "../../util/stringUtil";
import {resolveAppreciationId, useAppreciationVisible} from "./appreciationHooks";

interface AppreciationInputTextProps {
    /** option1: provide appreciation key */
    appreciationKey?: AppreciationKey;
    /** option2: provide appreciation page (and tab and section), to calculate key */
    page?: string;
    tab?: string;
    section?: string;
    /** flag, if the input should get focussed, when first rendered (and is also editable). */
    autoFocus?: boolean;
    placeholder?: string;
    /** flag, if a toast should be shown after save. */
    showToast?: boolean;
}

/**
 * Input Textarea for an Appreciation text.
 */
export default function AppreciationInputText({
    appreciationKey,
    page,
    tab,
    section,
    autoFocus = true,
    placeholder,
    showToast = true,
}: AppreciationInputTextProps) {
    const toast = useRef<Toast>(null);
    const {t} = useTranslation();

    const dispatch = useRevaDispatch();

    const effectiveAppreciationKey = resolveAppreciationId(appreciationKey, page, tab, section);
    const appreciationId = getAppreciationId(effectiveAppreciationKey);

    const storedValue = useSelector(selectAppreciationValueOrDefaultCurried(effectiveAppreciationKey));
    const [localValue, setLocalValue] = useState(storedValue || "");

    const readOnly = useSelector(selectReadOnly);

    const effectivePlaceholder = placeholder ?? "Würdigung ...";

    const appreciationVisible = useAppreciationVisible(effectiveAppreciationKey);

    // #18998: bei variativen Würdigungen kann der überschriebene Text zum Parameter der ehemals
    // gewählten Variante gehören, der sich zwischenzeitlich geändert haben kann.
    const hasStoredValue = useSelector(selectAppreciationHasStoredValueCurried(effectiveAppreciationKey));
    const serializedAppreciationKey = serializeKey(effectiveAppreciationKey, true);
    const showVariantOverrideWarning =
        hasStoredValue && APPRECIATION_KEYS_WITH_VARIANTS.includes(serializedAppreciationKey);
    const variantParameterKey = selectAppreciationVariantLabel(serializedAppreciationKey);
    const variantParameter = useSelector(selectSingleApiParamOutputDefCurried(variantParameterKey ?? ""));
    const selectLang = useSelectByCurrentLanguage();
    // Ohne Spec-Eintrag - oder ohne Namen darin - bleibt der rohe Parameter-Schlüssel als Beschriftung.
    const variantParameterName =
        variantParameter && selectLang(variantParameter.nameDe ?? "", variantParameter.nameEn ?? "");
    const variantParameterLabel = variantParameterName || variantParameterKey;

    useEffect(() => {
        setLocalValue(storedValue || "");
    }, [storedValue]);

    if (!appreciationVisible) {
        // Appreciation UI requires permission, except for the always-visible exception fields (#18607)
        return null;
    }

    const onValueChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        setLocalValue(e.target.value || "");
    };

    const onBlur = () => {
        const localValueClean = localValue || null;
        const isValueChanged = storedValue !== localValueClean;
        if (isValueChanged) {
            const isValueDeleted = storedValue && !localValueClean;
            void dispatch(changeAppreciationValueAsync(effectiveAppreciationKey, localValueClean));
            if (showToast) {
                if (isValueDeleted) {
                    toast.current?.show({
                        severity: "info",
                        summary: "Würdigung wurde gelöscht",
                        detail: "Es wird wieder der Default verwendet",
                        life: 4000,
                    });
                } else {
                    toast.current?.show({
                        severity: "success",
                        summary: "Änderungen an Würdigung gespeichert",
                        detail: truncateText(localValueClean ?? ""),
                        life: 4000,
                    });
                }
            }
        }
    };

    return (
        <>
            <Toast ref={toast} position="top-right" />

            <Tooltip target={`#appreciationWrapper_${appreciationId}`} position="top">
                {t("appreciation.tooltip")}
            </Tooltip>

            <div id={`appreciationWrapper_${appreciationId}`} className="w-full">
                <InputTextarea
                    id={`appreciationInput_${appreciationId}`}
                    className="w-full placeholder-light"
                    value={localValue}
                    placeholder={effectivePlaceholder}
                    onChange={onValueChange}
                    onBlur={onBlur}
                    cols={5}
                    rows={3}
                    maxLength={4096}
                    disabled={readOnly}
                    autoFocus={autoFocus && !readOnly}
                    autoResize
                    invalid={localValue.match(/\btodo\b/i) != null}
                />
                {showVariantOverrideWarning && (
                    <Message
                        severity="warn"
                        text={t("appreciation.variantOverrideWarning", {parameter: variantParameterLabel})}
                        className="w-full mt-1"
                    />
                )}
            </div>
        </>
    );
}
